import { useCallback, useEffect, useRef, useState } from 'react';
import { useApplicationContext } from '@/context/v1/Application/context';
import { useKimLhc } from '@/context/KimLhc';
import { useCalculateKimLhcScores } from '@/hooks/useCalculateKimLhcScores';
import { removeUndefinedValues } from '../utils/formValueFilters';

interface UseKimLhcAutoCalculateConfig<TStepFields, TScores, TScoresResponse> {
	watchedValues: TStepFields;
	initialScores: TScores;
	buildPayload: (stepFields: TStepFields, basePayload: BaseCalculationPayload) => CalculationPayload;
	transformScores: (response: TScoresResponse) => TScores;
}

export interface BaseCalculationPayload {
	id: string;
	file_id: string;
	company_id: string;
	organization_id: string;
}

interface CalculationPayload extends BaseCalculationPayload {
	frequency?: number;
	weight?: number;
	inclined_weight?: boolean;
	transporting_cargo_in_pairs?: boolean;
	cargo_handling_condition?: string;
	body_posture?: string;
	posture_additional_factor_twist?: string | null;
	posture_additional_factor_load_center?: string | null;
	posture_additional_factor_arms?: string | null;
	posture_additional_factor_hands?: string | null;
	work_condition_hands_position?: string | null;
	work_condition_transfer?: string | null;
	work_condition_spatial_condition?: string | null;
	work_condition_adverse?: string | null;
	work_condition_clothes?: string | null;
	work_condition_hold_load?: string | null;
	work_organization?: string;
}

export function useKimLhcAutoCalculate<TStepFields, TScores, TScoresResponse>(
	config: UseKimLhcAutoCalculateConfig<TStepFields, TScores, TScoresResponse>
) {
	const { organization, company } = useApplicationContext();
	const { report_id, file_id, onSetPartialScores } = useKimLhc();
	const { mutateAsync: calculateScores } = useCalculateKimLhcScores();

	const { watchedValues, initialScores, buildPayload, transformScores } = config;

	const [scores, setScores] = useState<TScores>(initialScores);
	const isFirstRender = useRef(true);

	const buildPayloadRef = useRef(buildPayload);
	const transformScoresRef = useRef(transformScores);

	const performScoresCalculation = useCallback(
		async (stepFields: TStepFields): Promise<void> => {
			if (!report_id || !file_id) {
				return;
			}

			const basePayload: BaseCalculationPayload = {
				id: report_id,
				file_id: file_id,
				company_id: company.id,
				organization_id: organization.id
			};

			const payload = buildPayloadRef.current(stepFields, basePayload);
			const cleanedPayload = removeUndefinedValues(payload);
			const response = await calculateScores(cleanedPayload as CalculationPayload);
			if (response) {
				setScores(transformScoresRef.current(response as TScoresResponse));
				const { man_score, woman_score } = response;
				onSetPartialScores({ man: man_score, woman: woman_score });
			}
		},
		[report_id, file_id, company.id, organization.id, calculateScores]
	);

	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;
			buildPayloadRef.current = buildPayload;
			transformScoresRef.current = transformScores;
			return;
		}

		performScoresCalculation(watchedValues);
	}, [watchedValues, performScoresCalculation]);

	return scores;
}
